import { Plus, Mail } from 'lucide-react';
import { useConnectedAccounts } from '../api/hooks';
import { analytics } from '../lib/analytics';

interface MemberCalendarActionsProps {
  member: {
    id: string;
    name: string;
    type: 'adult' | 'teen' | 'child';
    userId?: string | null;
  };
  currentUserId?: string;
  isAdmin: boolean;
  onAddSourceClick: (memberId: string, type: 'google' | 'microsoft' | 'ics' | 'compass') => void;
  onInviteClick: (memberId: string, memberName: string) => void;
}

export function MemberCalendarActions({
  member,
  currentUserId,
  isAdmin,
  onAddSourceClick,
  onInviteClick,
}: MemberCalendarActionsProps) {
  const { data: connectedAccounts } = useConnectedAccounts(member.id);

  const hasGoogleAccount = connectedAccounts && connectedAccounts.google.count > 0;
  const hasMicrosoftAccount = connectedAccounts && connectedAccounts.microsoft.count > 0;

  const canAddGoogle = connectedAccounts && connectedAccounts.google.count < connectedAccounts.google.max;
  const canAddMicrosoft = connectedAccounts && connectedAccounts.microsoft.count < connectedAccounts.microsoft.max;

  // Disable if member has userId but no account connected
  const googleDisabled = !!(member.userId && !hasGoogleAccount);
  const microsoftDisabled = !!(member.userId && !hasMicrosoftAccount);

  return (
    <div className="space-y-2">
      <p className="text-xs text-text-secondary">
        {member.userId === currentUserId ? 'Manage your' : `Manage ${member.name}'s`} Google/Microsoft calendars
      </p>

      {/* Show connected account counts if user has any accounts */}
      {connectedAccounts && (connectedAccounts.google.count > 0 || connectedAccounts.microsoft.count > 0) && (
        <div className="flex gap-3 text-xs text-text-secondary">
          {connectedAccounts.google.count > 0 && (
            <span>
              Google: {connectedAccounts.google.count}/{connectedAccounts.google.max}
            </span>
          )}
          {connectedAccounts.microsoft.count > 0 && (
            <span>
              Microsoft: {connectedAccounts.microsoft.count}/{connectedAccounts.microsoft.max}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {/* Only show Google button if can add more Google accounts OR member has no userId yet OR has Google accounts */}
        {(!member.userId || canAddGoogle || hasGoogleAccount) && (
          <button
            onClick={() => {
              if (googleDisabled) {
                analytics.calendarButtonDisabledViewed(member.id, 'google');
              } else {
                onAddSourceClick(member.id, 'google');
              }
            }}
            onMouseEnter={() => {
              if (googleDisabled) {
                analytics.calendarButtonDisabledViewed(member.id, 'google');
              }
            }}
            disabled={googleDisabled}
            title={googleDisabled ? "Connect a Google account first" : "Add Google Calendar"}
            className={`text-xs px-3 py-1.5 rounded-lg border border-border-default transition-colors flex items-center gap-1 ${
              googleDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-white cursor-pointer'
            }`}
          >
            <Plus className="w-3 h-3" />
            Google Calendar
          </button>
        )}

        {/* Only show Microsoft button if can add more Microsoft accounts OR member has no userId yet OR has Microsoft accounts */}
        {(!member.userId || canAddMicrosoft || hasMicrosoftAccount) && (
          <button
            onClick={() => {
              if (microsoftDisabled) {
                analytics.calendarButtonDisabledViewed(member.id, 'microsoft');
              } else {
                onAddSourceClick(member.id, 'microsoft');
              }
            }}
            onMouseEnter={() => {
              if (microsoftDisabled) {
                analytics.calendarButtonDisabledViewed(member.id, 'microsoft');
              }
            }}
            disabled={microsoftDisabled}
            title={microsoftDisabled ? "Connect a Microsoft account first" : "Add Microsoft Calendar"}
            className={`text-xs px-3 py-1.5 rounded-lg border border-border-default transition-colors flex items-center gap-1 ${
              microsoftDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-white cursor-pointer'
            }`}
          >
            <Plus className="w-3 h-3" />
            Microsoft Calendar
          </button>
        )}

        {/* ICS URL is always available */}
        <button
          onClick={() => onAddSourceClick(member.id, 'ics')}
          className="text-xs px-3 py-1.5 rounded-lg border border-border-default hover:bg-white transition-colors flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          ICS URL
        </button>

        {/* Compass is always available */}
        <button
          onClick={() => onAddSourceClick(member.id, 'compass')}
          className="text-xs px-3 py-1.5 rounded-lg border border-border-default hover:bg-white transition-colors flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Compass Education
        </button>
      </div>

      {/* Show invitation buttons for additional accounts if not at max */}
      {(canAddGoogle || canAddMicrosoft) && isAdmin && (
        <div className="pt-2 border-t border-border-light">
          <p className="text-xs text-text-secondary mb-2">
            Invite {member.userId === currentUserId ? 'yourself' : member.name} to connect additional accounts:
          </p>
          <div className="flex flex-wrap gap-2">
            {canAddGoogle && (
              <button
                onClick={() => {
                  if (googleDisabled) {
                    analytics.inviteTriggeredFromDisabledButton(member.id, 'google');
                  }
                  onInviteClick(member.id, member.name);
                }}
                className="text-xs px-3 py-1.5 rounded-lg bg-soft-cyan text-white hover:bg-opacity-90 transition-colors flex items-center gap-1"
              >
                <Mail className="w-3 h-3" />
                Google Account
              </button>
            )}
            {canAddMicrosoft && (
              <button
                onClick={() => {
                  if (microsoftDisabled) {
                    analytics.inviteTriggeredFromDisabledButton(member.id, 'microsoft');
                  }
                  onInviteClick(member.id, member.name);
                }}
                className="text-xs px-3 py-1.5 rounded-lg bg-soft-cyan text-white hover:bg-opacity-90 transition-colors flex items-center gap-1"
              >
                <Mail className="w-3 h-3" />
                Microsoft Account
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
